/* catalogBridge.js — convert a tenant catalog blob (from GET /api/catalog/state, the
 * CAT_SCHEMA=2 shape) into the engine's effective MODULE_LIBRARY + System-B catalog,
 * EXACTLY as the prototype's loadCatalog() does (lhc-app.html ~58821).
 *
 * loadCatalog overlay (verbatim, ~58836): for each CATALOG unit u, the base MODULE_LIBRARY
 * entry keyed by sku.toLowerCase() gets price/priceG1..G4 from u.p1..p4 and u.opts. Dims,
 * role, code, category stay from the BASE library — so geometry is unaffected by the catalog.
 *
 * Dealer-safe + no stale localStorage: this is a PURE function of the blob you pass in. The
 * caller supplies the dealer's blob (catalog_state is keyed dealer:<id> server-side).
 */
import { registerSidePanels } from "./sidePanelClearance";
import { MODULE_LIBRARY_BASE } from "./moduleLibraryBase";
import { benchConfigOf } from "../../catalog/lib/pricing";
import { familyGroupOf, TALL_MIN_M } from "./families";
export { familyGroupOf };

// System-B defaults the blob overlays/replaces (lhc-app.html ~58679/58721/59353 + ensureCatalogShape).
const DEFAULT_OVERSIZE = { overMm: 1000, addPct: 12 };
export const DEFAULT_PRICE_GROUPS = [
  { g: "G1", name: "Essential", finish: "Melamine", price: 85, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G2", name: "Classic", finish: "Laminate", price: 110, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G3", name: "Premium", finish: "Matt Laminate", price: 135, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G4", name: "Exclusive", finish: "Supermatt", price: 175, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G5", name: "Acrylic Gloss", finish: "Acrylic", price: 210, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G6", name: "Painted Matt", finish: "Painted", price: 265, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G7", name: "Painted Gloss", finish: "Painted", price: 320, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G8", name: "Veneer", finish: "Wood veneer", price: 390, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
  { g: "G9", name: "Premium", finish: "Specialty", price: 480, oversize: { ...DEFAULT_OVERSIZE }, doors: [] },
];
export const DEFAULT_DOOR_STYLES = [
  { name: "Slab", desc: "Flat slab door", addM2: 0 },
  { name: "Shaker", desc: "5-piece framed", addM2: 25 },
  { name: "Profiled", desc: "Routed / raised panel", addM2: 40 },
  { name: "Glass frame", desc: "Aluminium frame + glass", addM2: 60 },
];
export const DEFAULT_COUNTERTOPS = [
  { code: "CT-LAM-38", name: "Laminate worktop 38mm", material: "Laminate", edge: "Postformed", price: 45 },
  { code: "CT-COMP-20", name: "Compact laminate 20mm", material: "Compact laminate", edge: "Square", price: 90 },
  { code: "CT-QTZ-20", name: "Quartz composite 20mm", material: "Quartz", edge: "Polished", price: 240 },
  { code: "CT-QTZ-30", name: "Quartz composite 30mm", material: "Quartz", edge: "Mitred", price: 320 },
  { code: "CT-GRA-30", name: "Granite 30mm", material: "Granite", edge: "Polished", price: 300 },
  { code: "CT-STEEL", name: "Stainless steel worktop", material: "Stainless steel", edge: "Folded", price: 280 },
];
export const DEFAULT_PLINTH_MATERIALS = [
  { name: "Plastic", price: 14 }, { name: "Aluminum", price: 34 }, { name: "Door material", price: 18 },
];

const clone = (v) => JSON.parse(JSON.stringify(v));

/* ── 2026-07-23 single-source mandate: a technician-CREATED product (SKU not in
 * the base library) must still become a real, placeable, priceable def. The
 * def is SYNTHESIZED from the unit's own canonical data — dims (the "W×H×D"
 * dims string / SKU width segment, cm with mm tolerance, cfgDimsM parity),
 * role from the unit category, prices p1..p4, opts, model. ── */
export function unitDimsM(u) {
  // the DIMS string is authoritative; the SKU numeric segment is only the
  // fallback (a serial like TEST-001 must not read as a 1cm width)
  let w = null, h = null, d = null;
  const m = String(u.dims || "").match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
  if (m) { w = parseFloat(m[1]); h = parseFloat(m[2]); d = parseFloat(m[3]); }
  if (w == null) {
    // SKU width segment fallback. 2026-07-26 canonical-fidelity fix: segments
    // may carry a trailing variant suffix ("15 L" from "BU-D1S1-15 L-R") — the
    // LEADING number is the width; letter-first segments ("D1S1") never match.
    // Must stay identical to catalog/lib/units.unitWidthCm (technician parity).
    const segs = String(u.sku || "").split("-");
    for (const s of segs) {
      const seg = s.match(/^(\d+(?:\.\d+)?)(?![\d.])/);
      if (seg) { w = parseFloat(seg[1]); break; }
    }
  }
  if (!(h > 0)) h = (u.cat === "Wall Unit") ? 72 : (u.cat === "Tall Unit" || u.cat === "Column") ? 210 : (u.cat === "Island") ? 90 : 78;
  if (!(d > 0)) d = (u.cat === "Wall Unit") ? 35 : (u.cat === "Island") ? 90 : 57;
  const mm2cm = (x) => (x != null && x >= 320 ? x / 10 : x);
  w = mm2cm(w); h = mm2cm(h); d = mm2cm(d);
  const r3 = (x) => Math.round(x * 1000) / 1000;
  return { width: r3((w || 60) / 100), height: r3(h / 100), depth: r3(d / 100) };
}
/** familyOfUnit(u) — the technician's product line of a unit ("kitchens" | "systems" | "indoor" |
 *  "outdoor"), the same rule catalog/lib/units.unitGroup applies (batch 11). */
export function familyOfUnit(u) {
  return String((u && u.group) || "kitchens").toLowerCase();
}
/* the wizard family word → the technician's product line: engine/families.familyGroupOf (re-exported above) */
const _famLibCache = new WeakMap();
/** filterLibForFamily(lib, wizardFamily) — the defs of ONE product line (memoised per lib):
 *  a Systems project lists wardrobes and beds, never the kitchen units, and vice versa. */
export function filterLibForFamily(lib, wizardFamily) {
  if (!lib) return lib;
  const g = familyGroupOf(wizardFamily);
  let byFam = _famLibCache.get(lib);
  if (!byFam) { byFam = {}; _famLibCache.set(lib, byFam); }
  if (byFam[g]) return byFam[g];
  const out = {};
  Object.keys(lib).forEach((k) => { const d = lib[k]; if (d && String(d.family || "kitchens") === g) out[k] = d; });
  byFam[g] = out;
  return out;
}
export function roleForCat(cat, heightM) {
  const c = String(cat || "").toLowerCase();
  if (/wall/.test(c)) return "cabinet-wall";
  if (/tall|column/.test(c)) return "cabinet-tall";
  // a unit as tall as a column IS a column, whatever its category says (a "stock"
  // kitchen SKU, a wardrobe, a walk-in bay): never a worktop carrier (2026-09-29)
  if (Number(heightM) >= TALL_MIN_M) return "cabinet-tall";
  return "cabinet-base";
}
export function synthesizeDef(u) {
  const dims = unitDimsM(u);
  const role = roleForCat(u.cat, dims.height);
  const family = familyOfUnit(u);
  return {
    code: String(u.sku || "").toUpperCase(),
    name: u.name || u.sku,
    role,
    family,
    // a furniture line groups by its sub-category (Sofas, Wardrobes…), the kitchen by role
    category: family !== "kitchens" ? (u.sub || u.cat || "Modules") : role === "cabinet-wall" ? "wall" : role === "cabinet-tall" ? "tall" : "base",
    width: dims.width, height: dims.height, depth: dims.depth,
    price: u.p1 != null ? u.p1 : (u.price || 0),
    ...(u.desc != null ? { desc: u.desc } : {}),
    ...(u.geometry ? { geometry: u.geometry } : (() => { const g = inferGeometryFromName(u.name); return g ? { geometry: g } : {}; })()),
    ...(u.frontLayouts ? { frontLayouts: u.frontLayouts } : {}),
  };
}



/* PRODUCT DEFAULT CONFIG (2026-08-11 "what we set in the technician goes to
 * the configurator EXACTLY the same"): the bench's live-edited u.cfg becomes
 * def.defaultConfig via benchConfigOf — the SAME adapter the bench itself
 * prices/renders with, so a newly placed module starts as the exact bench
 * setup (opening system, handle, per-front positions, colours, worktop…). */
function defaultConfigFor(blob, u) {
  try {
    if (!u || !u.cfg) return null;
    const cfg = benchConfigOf(blob, u, u.cfg);
    return (cfg && Object.keys(cfg).length) ? cfg : null;
  } catch (e) { return null; }
}

/* GEOMETRY FROM THE NAME (2026-08-11 "it has 2 drawers and it opens like
 * it's 1"): a technician product named "2 drawers" / "3 Doors" whose Geo
 * fields were never filled still builds the RIGHT fronts - the name is the
 * technician's declared structure. Explicit u.geometry always wins; a code
 * with a D-pattern (BU-Dr3-...) never needed this (elevation parses it). */
export function inferGeometryFromName(name) {
  const s = String(name || "");
  let m = s.match(/(\d+)\s*(?:drawer|cassett|sirtar)/i);
  if (m) return { drawers: Math.max(1, Math.min(8, parseInt(m[1], 10))) };
  if (/drawer|cassett|sirtar/i.test(s)) return { drawers: 1 };
  m = s.match(/(\d+)\s*(?:door|anta|ante|dyer|porta)/i);
  if (m) return { doors: Math.max(1, Math.min(6, parseInt(m[1], 10))) };
  return null;
}

/* ── PHASE 3: shared component library + collection inheritance ─────────────
 * blob.compLib     = [{ id "CMP-N", name, role (front|handle|…), model glbUrl }]
 *                    — components uploaded ONCE, referenced by any unit.
 * blob.collections = [{ id "col-N", name, frontLayouts?, components?: {role:
 *                    {source:"lib", ref} | {source:"glb", model}} }]
 * u.collection     = collection id; u WITHOUT its own key INHERITS:
 *   · frontLayouts — unit's non-empty list wins, else the collection's;
 *   · model3d.components — merged per-role, unit wins; a modelless unit that
 *     gains components resolves to COMPONENT mode, a modelled one to HYBRID.
 * "lib" component refs materialize here (both bridges, JS ≡ PY) so the
 * resolver + validators + pricing stay blob-free. Unresolvable refs drop the
 * component (undefined — same rule as a GLB source without a file). */
export function compLibEntry(blob, role, ref) {
  const e = (((blob || {}).compLib) || []).find((x) => x && String(x.id) === String(ref));
  return e && e.model && (!role || e.role === role) ? e : null;
}
function materializeComponents(blob, comps) {
  const out = {};
  Object.keys(comps || {}).forEach((role) => {
    const c = comps[role];
    if (!c || typeof c !== "object") return;
    if (c.source === "lib") {
      const e = compLibEntry(blob, role, c.ref);
      if (e) out[role] = { source: "glb", model: e.model, ref: e.id };
      return;                                        // missing ref → undefined component
    }
    if (c.source === "entity") {
      // COMPONENT REGISTRY (V2): any registry entity that owns a GLB can BE a
      // module component. The eid is resolved here so every downstream reader
      // (resolver, 3D, Present, validation) keeps seeing a plain glb source.
      const ent = ((blob || {}).entities || {})[c.ref];
      const model = ent && ent.media && ent.media.model;
      if (model) out[role] = { source: "glb", model: model, ref: c.ref };
      return;                                        // entity gone or model-less → dropped, never guessed
    }
    out[role] = c;
  });
  return out;
}
function collectionOf(blob, u) {
  const id = u && u.collection;
  if (!id) return null;
  return (((blob || {}).collections) || []).find((c) => c && String(c.id) === String(id)) || null;
}
export function effectiveFrontLayouts(blob, u) {
  const own = (u && u.frontLayouts) || [];
  if (own.length) return own;
  const col = collectionOf(blob, u);
  const inh = (col && col.frontLayouts) || [];
  return inh.length ? inh : null;
}
export function effectiveModel3d(blob, u) {
  const m3 = (u && u.model3d) || null;
  const col = collectionOf(blob, u);
  const merged = { ...((col && col.components) || {}), ...((m3 && m3.components) || {}) };
  const mat = materializeComponents(blob, merged);
  const mode = (m3 && m3.mode) || (Object.keys(mat).length ? (u && u.model ? "hybrid" : "component") : null);
  if (!mode) return null;
  return { mode, components: mat };
}

/** buildModuleLibrary(blob): base library with the tenant catalog's prices + opts overlaid
 *  by SKU — matches loadCatalog()'s MODULE_LIBRARY overlay exactly.
 *
 *  2026-07-19 PUBLICATION RULE (user mandate "cabinets are taken from the
 *  Cabinet Technician"): the offerable library is the technician's catalog —
 *    · any unit flagged inCatalog → ONLY those SKUs are offered;
 *    · none flagged (uncurated tenant) → every technician catalog SKU;
 *    · no blob/carcass at all → the full base library (dealer-safe fallback).
 *  Geometry/roles always come from the base def matched by SKU; placements of
 *  since-unpublished SKUs are kept alive via ensureDefsFor (engine load). */
/* ── THE publication rule: ONE predicate for every consumer.
 * 2026-08-04 user mandate (publication split): "In Catalog" is MEMBERSHIP
 * only; a product entering the Catalog arrives NOT live, and ONLY the
 * explicit live click publishes it to the configurator. So published =
 * inCatalog AND status === "live" — strictly. Legacy blobs where an
 * inCatalog unit still carried an implicit status ("np"/"priced"/missing —
 * published under the old lenient rule) are stamped "live" once by the
 * pubSplitV1 migration in catalog/lib/persistence.js, so nothing that was
 * published silently unpublishes. Model selector, wizard bridge and this
 * library must all import these — never re-implement the filter. (JS ≡ PY.)
 * isUnitLive stays lenient — it only feeds the uncurated-tenant fallback. ── */
export const isUnitLive = (u) => !!u && u.status !== "off";
export const isUnitPublished = (u) => !!u && !!u.inCatalog && u.status === "live";

export function buildModuleLibrary(blob) {
  const lib = clone(MODULE_LIBRARY_BASE);
  /* side panels are physical geometry — their thickness lives in the catalog,
     which the engine layer never sees; register it once per catalog load */
  registerSidePanels(blob);
  const carcass = (blob && blob.carcass) || [];
  const published = carcass.filter(isUnitPublished);
  // PUBLICATION IS THE ONLY GATE (2026-08-09). The old fallback offered every
  // LIVE row whenever nothing was published — so a technician who imported a
  // price list (sofas, tables, anything) found all of it in the kitchen
  // configurator without ever publishing it. A tenant WITH products now sees
  // exactly what they published, and nothing else; the lenient fallback
  // survives only for a tenant with no products at all (the demo library).
  const allow = published.map((u) => String((u && u.sku) || "").toLowerCase()).filter(Boolean);
  if (carcass.length) {
    // a tenant with their own catalog never inherits the base demo modules —
    // an empty result means "publish something", not "show everything"
    const keep = new Set(allow);
    Object.keys(lib).forEach((k) => { if (!keep.has(k)) delete lib[k]; });
  } else if (blob && Array.isArray(blob.carcass)) {
    // HONEST FALLBACK (2026-08-18 hardcode purge): a REAL tenant blob (it
    // always carries a carcass ARRAY, even empty) whose catalog is simply
    // empty gets an EMPTY library — never the 121 priced demo SKUs presented
    // as their own. The demo library survives ONLY when there is no real blob
    // (catalog fetch failed → the engine substitutes {} and raises
    // catalogError; dev without a server).
    Object.keys(lib).forEach((k) => { delete lib[k]; });
  }
  carcass.forEach((u) => {
    const k = String(u.sku || "").toLowerCase();
    let def = lib[k];
    // technician-CREATED SKU (no base def): synthesize one from the unit's own
    // canonical data — but only when the publication rule offers this unit.
    if (!def && k && allow.indexOf(k) >= 0) { def = synthesizeDef(u); lib[k] = def; }
    if (!def) return;
    const bp = u.p1 != null ? u.p1 : u.price != null ? u.price : def.price;
    if (bp != null) {
      def.price = bp;
      def.priceG1 = u.p1 != null ? u.p1 : bp;
      if (u.p2 != null) def.priceG2 = u.p2;
      if (u.p3 != null) def.priceG3 = u.p3;
      if (u.p4 != null) def.priceG4 = u.p4;
    }
    if (u.opts) def.opts = u.opts;
    def.family = familyOfUnit(u);        // batch 11: the product line the library filters by
    if (u.model) def.model = u.model;   // technician-attached GLB (2026-07-20)
    const _em3 = effectiveModel3d(blob, u);   // Phase 3: lib refs + collection components materialized
    if (_em3) def.model3d = _em3;
    if (u.model3dMeta) def.model3dMeta = u.model3dMeta; // upload-time GLB analysis (deterministic hybrid decision)
    if (u.images) def.images = u.images; // canonical product imagery (2026-07-25)
    if (u.desc != null) def.desc = u.desc; // canonical product description (Phase A)
    if (u.geometry) def.geometry = u.geometry; // explicit doors/drawers/hinge (RC polish)
    else { const _gN = inferGeometryFromName(u.name); if (_gN) def.geometry = _gN; }
    const _dc = defaultConfigFor(blob, u);      // bench setup = new-module default
    if (_dc) def.defaultConfig = _dc;
    if (Number(u.leadDays) > 0) def.leadDays = Number(u.leadDays); // production lead time (Phase 6)
    const _efl = effectiveFrontLayouts(blob, u);   // Phase 2 variants, Phase 3 collection-inherited
    if (_efl) def.frontLayouts = _efl;
    // 2026-07-26 real-catalog fidelity: the technician's RENAME and explicit
    // DIMS are canonical for BASE-library SKUs too (previously only price/opts/
    // model/desc flowed — a renamed "1 door · 1 shelf" kept the base name).
    if (u.name && u.name !== u.sku) def.name = u.name;
    if (/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/.test(String(u.dims || ""))) {
      const dd = unitDimsM(u);
      def.width = dd.width; def.height = dd.height; def.depth = dd.depth;
    }
  });

  /* FREE-STANDING SIDE PANELS (2026-08-29 user: Room → Side Panels "for
   * cases like this" — a tall end panel beside a run). Every technician
   * side-panel entry is ALSO offered as a placeable board module
   * ("sp-free-<tok>"): a real placement, so island drag + the 0mm
   * edge-to-edge magnets, the Edit Position panel, pricing, save/load and
   * both renderers all come from the existing machinery. Role cabinet-tall =
   * floor-standing with no kick/counter parts; code SPNL-<i> can never match
   * a door/drawer SKU pattern. Mirrored in backend/catalog_bridge.py so the
   * publish gate + server pricing accept the same ids. */
  const _spList = (blob && blob.sidePanels) || [];
  _spList.forEach((sp2, i2) => {
    if (!sp2) return;
    const tok2 = sp2.uid != null ? "u" + sp2.uid : String(i2);
    lib["sp-free-" + tok2] = {
      code: "SPNL-" + (i2 + 1), name: (sp2.name || "Side panel") + " · panel",
      category: "Panels", subcategory: "Side panels", role: "cabinet-tall",
      width: Math.max(0.006, (Number(sp2.thicknessMm) || 18) / 1000),
      height: 2.2, depth: 0.6,
      price: sp2.price || 0, priceG1: sp2.price || 0,
      ...(sp2.model ? { model: sp2.model } : {}),
      sidePanelEntry: true,
    };
  });
  return lib;
}

/** ensureDefsFor(lib, types, blob?): re-add BASE defs for already-placed module
 *  types that the publication filter excluded — existing projects must never
 *  lose their placements' geometry/pricing when the technician unpublishes a
 *  SKU. When the blob still carries the unit, its price/opts overlay is
 *  re-applied so the technician's pricing keeps winning over the base. */
export function ensureDefsFor(lib, types, blob) {
  const bySku = {};
  (((blob || {}).carcass) || []).forEach((u) => { const k = String((u && u.sku) || "").toLowerCase(); if (k) bySku[k] = u; });
  (types || []).forEach((t) => {
    const k = String(t || "").toLowerCase();
    if (!k || lib[k]) return;
    // resurrect from the base library, or SYNTHESIZE for technician-created
    // SKUs — a placed instance must never lose its geometry/pricing (§18).
    // Even a FULLY DELETED product keeps a safe tombstone def (renders at its
    // SKU-derived size, prices 0, clearly labelled) — deletion never corrupts
    // or silently empties an existing project.
    const u = bySku[k];
    const def = MODULE_LIBRARY_BASE[k] ? clone(MODULE_LIBRARY_BASE[k])
      : u ? synthesizeDef(u)
      : { ...synthesizeDef({ sku: k, cat: "Base Unit" }), name: k.toUpperCase() + " (removed from catalog)", price: 0, priceG1: 0, _removed: true };
    if (u) {
      const bp = u.p1 != null ? u.p1 : u.price != null ? u.price : def.price;
      if (bp != null) {
        def.price = bp;
        def.priceG1 = u.p1 != null ? u.p1 : bp;
        if (u.p2 != null) def.priceG2 = u.p2;
        if (u.p3 != null) def.priceG3 = u.p3;
        if (u.p4 != null) def.priceG4 = u.p4;
      }
      if (u.opts) def.opts = u.opts;
      if (u.model) def.model = u.model;
      const _em3 = effectiveModel3d(blob, u);   // registry (+ Phase 3 inheritance) survives unpublish (§18)
      if (_em3) def.model3d = _em3;
      if (u.model3dMeta) def.model3dMeta = u.model3dMeta;
      if (u.images) def.images = u.images;
      if (u.desc != null) def.desc = u.desc;
      if (u.geometry) def.geometry = u.geometry;
      else { const _gN2 = inferGeometryFromName(u.name); if (_gN2) def.geometry = _gN2; }
      const _dc2 = defaultConfigFor(blob, u);
      if (_dc2) def.defaultConfig = _dc2;
      if (Number(u.leadDays) > 0) def.leadDays = Number(u.leadDays);
      const _efl = effectiveFrontLayouts(blob, u);
      if (_efl) def.frontLayouts = _efl;
      if (u.name && u.name !== u.sku) def.name = u.name;   // technician rename is canonical
      if (/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/.test(String(u.dims || ""))) {
        const dd = unitDimsM(u);
        def.width = dd.width; def.height = dd.height; def.depth = dd.depth;
      }
    }
    // Resurrected defs exist ONLY so placed instances keep geometry/pricing —
    // they are not offered for NEW placements (the library hides them and the
    // server 422s them; 2026-07-26 master E2E finding).
    def._unoffered = true;
    lib[k] = def;
  });
  return lib;
}

/** buildSystemBCatalog(blob): the System-B arrays (PRICE_GROUPS overlaid onto defaults by g;
 *  the rest replaced when present). GLB/model references are preserved on CATALOG[].model. */
export function buildSystemBCatalog(blob) {
  const b = blob || {};
  /* HONEST FALLBACK (2026-08-18): with a real blob, an unauthored section is
   * EMPTY (or the group's own values) — the demo price book (€85–480/m² doors,
   * €45–320/m worktops, €14–34/m plinths) is served ONLY when there is no
   * blob at all. A tenant group without a price now prices 0 (visible as
   * "unpriced") instead of silently inheriting a demo € rate. */
  const hasRealBlob = !!(blob && (Array.isArray(blob.groups) || Array.isArray(blob.carcass)));
  const PRICE_GROUPS = hasRealBlob
    ? (b.groups || []).map((sg) => ({
        g: sg.g, name: sg.name || ("PG" + sg.g), finish: sg.finish || "",
        price: sg.price != null ? sg.price : 0,
        oversize: sg.oversize ? clone(sg.oversize) : { ...DEFAULT_OVERSIZE },
        doors: sg.doors ? clone(sg.doors) : [],
      }))
    : clone(DEFAULT_PRICE_GROUPS);
  return {
    PRICE_GROUPS,
    HANDLES: b.handles || [],
    GOLA: b.gola || [],
    CATALOG: b.carcass || [],          // System-B units, incl. .model (GLB) refs — preserved
    APPLIANCES: b.appliances || [],
    CARCASS_COLORS: b.colors || [],
    PLINTH: b.plinth || [],
    PLINTH_MATERIALS: b.pmat || (hasRealBlob ? [] : clone(DEFAULT_PLINTH_MATERIALS)),
    DOOR_STYLES: b.dstyles || (hasRealBlob ? [] : clone(DEFAULT_DOOR_STYLES)),
    COUNTERTOPS: b.countertops || (hasRealBlob ? [] : clone(DEFAULT_COUNTERTOPS)),
    LIGHTING: b.lighting || [],
  };
}
